import { apiClient } from "./client";

// ---------------------------------------------------------------------------
// REGISTRO GIORNALE DEGLI AFFARI
//
// Non è un modulo dell'applicazione: è un registro di legge (art. 120 TULPS,
// contenuto fissato dall'art. 219 del R.D. 635/1940). Le colonne sono quelle
// che la norma impone, e coincidono con il foglio Excel che EVO tiene oggi.
//
// La maggior parte delle righe non si crea da qui: nasce da sola quando un
// contratto viene firmato. Questo modulo serve a LEGGERE il registro, a
// completarlo — compenso percepito ed esito si sanno dopo — e a scrivere a
// mano l'affare che da un contratto Cash Management non viene, incluso lo
// storico caricato a posteriori.
// ---------------------------------------------------------------------------

/**
 * L'esito dell'operazione.
 *
 * I primi tre sono quelli che compaiono davvero sul registro di EVO;
 * `interrotto_anticipatamente` è stato aggiunto perché `concluso` significa
 * «arrivato in fondo» e non può coprire anche l'affare finito prima.
 */
export type EsitoAffare =
  | "in_corso_di_esecuzione"
  | "rinnovo_anno_precedente"
  | "concluso"
  | "interrotto_anticipatamente";

export const ESITO_LABEL: Record<EsitoAffare, string> = {
  in_corso_di_esecuzione: "In corso di esecuzione",
  rinnovo_anno_precedente: "Rinnovo anno precedente",
  concluso: "Concluso",
  interrotto_anticipatamente: "Interrotto anticipatamente",
};

export interface RigaRegistroAffari {
  id: string;
  /**
   * Il numero d'ordine sta in due colonne e non nella stringa che si legge sul
   * registro: una stringa non si ordina (`10-24` verrebbe prima di `2-24`) e
   * non si vincola a essere unica per anno. `numero_ordine` è la forma
   * mostrabile, composta dal backend.
   */
  anno: number;
  progressivo: number;
  numero_ordine: string;
  /**
   * PUÒ MANCARE, ed è voluto: sul registro storico otto righe su dodici non
   * hanno una data. `null` dice «non risulta», che è diverso da qualunque data
   * inventata.
   */
  data: string | null;
  generalita_a: string;
  generalita_b: string | null;
  natura_operazione: string;
  /** `null` e non `0`: «pattuito niente» e «non risulta un importo» differiscono. */
  compenso_pattuito: string | null;
  compenso_percepito: string | null;
  esito_affare: EsitoAffare;
  contratto_id: string | null;
  note: string | null;
  created_at: string;
  contratto?: {
    id: string;
    riferimento_contratto: string | null;
    cliente: { id: string; nome: string } | null;
  } | null;
  creatore?: { id: string; full_name: string | null; email: string } | null;
}

export interface RegistroAffari {
  righe: RigaRegistroAffari[];
  /** Gli anni che hanno almeno una riga, per il selettore. */
  anni: number[];
  esiti: EsitoAffare[];
  natura_predefinita: string;
}

/** Il registro, tutto o di un anno solo. */
export async function listRegistro(anno?: number): Promise<RegistroAffari> {
  const { data } = await apiClient.get<RegistroAffari>("/registro-affari", {
    params: anno ? { anno } : {},
  });
  return data;
}

export interface NuovaRiga {
  /**
   * Senza `data` bisogna indicare `anno`: regge il numero d'ordine e non si
   * può derivare da niente altro. Sul registro di carta lo si legge dal numero
   * stesso (`1-24` → 2024).
   */
  data?: string | null;
  anno?: number;
  generalita_a: string;
  generalita_b?: string | null;
  natura_operazione: string;
  compenso_pattuito?: string | null;
  compenso_percepito?: string | null;
  esito_affare?: EsitoAffare;
  note?: string | null;
  /** Il contratto di provenienza, quando la riga si scrive a mano per lo storico. */
  contratto_id?: string | null;
}

export async function creaRiga(riga: NuovaRiga): Promise<{ riga: RigaRegistroAffari }> {
  const { data } = await apiClient.post<{ riga: RigaRegistroAffari }>("/registro-affari", riga);
  return data;
}

/**
 * Aggiorna una riga. **Parziale davvero**: quello che non mandi resta com'era.
 *
 * `anno`, `progressivo` e `contratto_id` non si toccano — il numero d'ordine e
 * la provenienza di una registrazione non sono campi da form. La `data` sì, ma
 * deve restare nel suo anno: per spostare l'affare in un altro anno la riga va
 * cancellata e riscritta.
 */
export async function aggiornaRiga(
  id: string,
  campi: Partial<Omit<NuovaRiga, "anno" | "contratto_id">>
): Promise<{ riga: RigaRegistroAffari }> {
  const { data } = await apiClient.patch<{ riga: RigaRegistroAffari }>(`/registro-affari/${id}`, campi);
  return data;
}

/** Solo admin. Non è un'operazione come le altre: toglie una registrazione di legge. */
export async function eliminaRiga(id: string): Promise<{ success: true }> {
  const { data } = await apiClient.delete<{ success: true }>(`/registro-affari/${id}`);
  return data;
}

// ---------------------------------------------------------------------------
// L'ESTRATTO DEL PERIODO
//
// Il documento che si firma e si marca temporalmente fuori dall'app. Due
// chiamate sulla stessa selezione: una conta, l'altra scarica — perché il
// conteggio va visto PRIMA di generare. Qui si sceglie cosa finirà dentro un
// documento firmato, e un estratto incompleto ce ne si accorge dopo la firma.
// ---------------------------------------------------------------------------

/** Anno solare, oppure intervallo libero: uno dei due, non tutti e due. */
export type PeriodoEstratto = { anno: number } | { da: string; a: string };

export interface AnteprimaEstratto {
  periodo: { da: string; a: string };
  totale: number;
  /**
   * Quante righe sono dentro pur non avendo una data: ci rientrano per l'anno
   * del numero d'ordine. Si mostra perché è la differenza fra questo estratto
   * e uno che filtrasse solo sulle date.
   */
  senza_data: number;
  per_anno: { anno: number; quante: number; dal: string; al: string }[];
}

function paramsPeriodo(p: PeriodoEstratto): Record<string, string | number> {
  return "anno" in p ? { anno: p.anno } : { da: p.da, a: p.a };
}

export async function anteprimaEstratto(p: PeriodoEstratto): Promise<AnteprimaEstratto> {
  const res = await apiClient.get<AnteprimaEstratto>("/registro-affari/estratto", {
    params: paramsPeriodo(p),
  });
  return res.data;
}

/**
 * Scarica il PDF. Passa da axios e non da un link diretto perché serve il
 * token di autenticazione — stesso motivo del report e del rapportino — e per
 * lo stesso motivo il nome del file lo mette il chiamante: da un blob il
 * browser non legge il `Content-Disposition` della risposta.
 */
export async function scaricaEstratto(p: PeriodoEstratto, nomeFile: string): Promise<void> {
  const res = await apiClient.get("/registro-affari/estratto.pdf", {
    params: paramsPeriodo(p),
    responseType: "blob",
  });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = nomeFile;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// GLI ARCHIVI DEI PERIODI VIDIMATI (Fase 6)
//
// Quello che torna da Aruba — il PDF firmato, il `.tsd` della marca, il `.p7m`
// della firma — si carica qui e resta legato al periodo che rappresenta. I tre
// file sono una catena (il `.p7m` firma il `.tsd`, che contiene il PDF), per
// questo solo il PDF è obbligatorio: è l'unico che si apre senza strumenti.
//
// L'app conserva e ne fa una copia su SharePoint. NON verifica né la firma né
// la marca.
// ---------------------------------------------------------------------------

export type StatoVidimazione = "vidimato" | "parziale" | "scoperto" | "in_corso";

export const STATO_VIDIMAZIONE_LABEL: Record<StatoVidimazione, string> = {
  vidimato: "Vidimato",
  parziale: "Vidimato in parte",
  scoperto: "Non vidimato",
  in_corso: "Anno in corso",
};

/** Quanto di un anno del registro è coperto da archivi. */
export interface CoperturaAnno {
  anno: number;
  stato: StatoVidimazione;
  registrazioni: number;
  /** Gli intervalli che nessun archivio copre: vuoto se vidimato o in corso. */
  scoperture: { da: string; a: string }[];
}

export interface ArchivioRegistro {
  id: string;
  periodo_da: string;
  periodo_a: string;
  file_pdf_url: string;
  file_tsd_url: string | null;
  file_p7m_url: string | null;
  data_marcatura: string;
  /** Quando la copia su SharePoint è riuscita l'ultima volta; `null` se mai. */
  copia_esterna_at: string | null;
  /** Il perché dell'ultimo tentativo di copia fallito; `null` se è andato bene. */
  copia_esterna_errore: string | null;
  created_at: string;
  creatore: { id: string; full_name: string | null; email: string } | null;
  /** Le registrazioni del periodo, contate come fa l'estratto. */
  registrazioni: number;
  percorso_esterno: string;
}

export type FileArchivio = "pdf" | "tsd" | "p7m";

export async function listArchivi(): Promise<{
  archivi: ArchivioRegistro[];
  anni: CoperturaAnno[];
  sharepoint_configurato: boolean;
}> {
  const res = await apiClient.get("/registro-affari/archivi");
  return res.data;
}

export async function creaArchivio(d: {
  periodo_da: string;
  periodo_a: string;
  data_marcatura: string;
  pdf: File;
  tsd: File | null;
  p7m: File | null;
}): Promise<ArchivioRegistro> {
  const form = new FormData();
  form.append("periodo_da", d.periodo_da);
  form.append("periodo_a", d.periodo_a);
  form.append("data_marcatura", d.data_marcatura);
  form.append("pdf", d.pdf);
  if (d.tsd) form.append("tsd", d.tsd);
  if (d.p7m) form.append("p7m", d.p7m);
  // L'header va forzato come in `anteprimaSchedaCliente` (api/clients.ts):
  // `apiClient` nasce con `Content-Type: application/json`, e una FormData
  // spedita così arriva senza boundary — multer non vede nessun file.
  const { data } = await apiClient.post<{ archivio: ArchivioRegistro }>("/registro-affari/archivi", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data.archivio;
}

/** Ritenta la copia su SharePoint. Ripetibile: SharePoint sostituisce, non duplica. */
export async function ricopiaArchivio(id: string): Promise<ArchivioRegistro> {
  const { data } = await apiClient.post<{ archivio: ArchivioRegistro }>(`/registro-affari/archivi/${id}/ricopia`);
  return data.archivio;
}

/**
 * Toglie un archivio dall'elenco. Il motivo è obbligatorio e va nel corpo:
 * axios lo manda con una DELETE solo se sta in `data`.
 */
export async function eliminaArchivio(id: string, motivo: string): Promise<{ copia_esterna_conservata: boolean }> {
  const { data } = await apiClient.delete<{ copia_esterna_conservata: boolean }>(`/registro-affari/archivi/${id}`, {
    data: { motivo },
  });
  return data;
}

/** Il nome con cui un file dell'archivio si scarica: lo stesso che ha su SharePoint. */
export function nomeFileArchivio(a: ArchivioRegistro, tipo: FileArchivio): string {
  return `RGA_EVOsrl_${a.periodo_da.slice(0, 10)}_${a.periodo_a.slice(0, 10)}.${tipo}`;
}
